/*
Shared pieces of the releases calendar (timeline and month): the studio logo, the model
chip (logo + model name — the logo says whose model it is), the expected-release chip
(dashed: one estimate from the studio's rhythm and the rumours about it), and the contents
of the details windows. Text goes in only through textContent.
*/
import { element, safeUrl } from './eventsData';
import {
    capitalize,
    dateNote,
    formatCachedPrice,
    formatPrice,
    formatTokens,
    longDate,
    relativeDays,
    shortDate,
    studioInitial,
} from './releasesData';

const LOGO_PATH = '/assets/images/studios/';
// Credit when the radio's answer has none: the benchmarks come from Epoch AI by default.
const EPOCH_SOURCE = { name: 'Epoch AI', url: 'https://epoch.ai/benchmarks', license: 'CC BY 4.0' };
// A one-colour logo in the studio column takes the brand colour only when it reads on the
// light column (contrast with white at least 3: Anthropic's #D97757 ≈ 3.2 passes, Xiaomi's
// #FF6900 ≈ 2.6 does not — it goes the column text colour).
const LOGO_MIN_CONTRAST = 3;

/** Studio logo from assets/images/studios/<id>.svg; a letter in the studio colour if none
 * (the colour is a checked #rrggbb from the radio config, see normalizeCalendar). */
export function studioMark(studio) {
    const mark = element('span', 'th-rel-mark');
    const logo = element('img', 'th-rel-mark__logo');
    logo.src = logoUrl(studio);
    logo.alt = studio.name || studio.id;
    logo.width = 16;
    logo.height = 16;
    logo.decoding = 'async';
    logo.loading = 'lazy';
    logo.addEventListener('error', () => {
        mark.replaceChildren(letterMark(studio));
    }, { once: true });
    mark.append(logo);
    return mark;
}

function logoUrl(studio) {
    return `${LOGO_PATH}${encodeURIComponent(studio.id)}.svg`;
}

/**
 * Studio logo for the studio column. A colour logo (Google, Meta) stays as it is; a
 * one-colour logo (its SVG paints with currentColor — an <img> draws it black) becomes a
 * shape in the brand colour when that reads on the column, the column text colour
 * otherwise. Which logo is one-colour is read from the file itself, so a new logo needs no list.
 */
export function columnStudioMark(studio) {
    const mark = studioMark(studio);
    const url = logoUrl(studio);
    isOneColourLogo(url).then((oneColour) => {
        if (!oneColour || !mark.querySelector('.th-rel-mark__logo')) {
            return;
        }
        const shape = element('span', 'th-rel-mark__shape');
        shape.style.setProperty('--logo', `url("${url}")`);
        shape.style.setProperty('--logo-color', readableBrandColour(studio.color));
        shape.setAttribute('role', 'img');
        shape.setAttribute('aria-label', studio.name || studio.id);
        mark.replaceChildren(shape);
    });
    return mark;
}

const oneColourChecks = new Map();

/** Whether the logo SVG paints with currentColor; the file is already in the browser cache. */
function isOneColourLogo(url) {
    if (!oneColourChecks.has(url)) {
        oneColourChecks.set(url, fetch(url)
            .then((response) => (response.ok ? response.text() : ''))
            .then((svg) => svg.includes('currentColor'))
            .catch(() => false));
    }
    return oneColourChecks.get(url);
}

/** The brand colour if it contrasts with white enough (LOGO_MIN_CONTRAST), else the column text. */
function readableBrandColour(color) {
    if (!color || (1.05 / (relativeLuminance(color) + 0.05)) < LOGO_MIN_CONTRAST) {
        return 'var(--rel-column-text)';
    }
    return color;
}

/** WCAG relative luminance of a #rrggbb colour (0 black … 1 white). */
function relativeLuminance(color) {
    const [red, green, blue] = [1, 3, 5].map((start) => {
        const channel = Number.parseInt(color.slice(start, start + 2), 16) / 255;
        return channel <= 0.03928 ? channel / 12.92 : ((channel + 0.055) / 1.055) ** 2.4;
    });
    return (0.2126 * red) + (0.7152 * green) + (0.0722 * blue);
}

function letterMark(studio) {
    const letter = element('span', 'th-rel-mark__letter', studioInitial(studio));
    letter.setAttribute('aria-label', studio.name || studio.id);
    if (studio.color) {
        letter.style.background = studio.color;
    }
    return letter;
}

/** A model: logo + name; the details window (access, benchmarks…) opens on hover or tap. */
export function modelChip(release, studio) {
    const chip = element('button', 'th-rel-chip');
    chip.type = 'button';
    chip.dataset.pop = `release:${release.id}`;
    chip.append(studioMark(studio), element('span', 'th-rel-chip__name', release.name));
    return chip;
}

/** The studio's expected next release: "~Oct 5–31". */
export function expectedChip(studio) {
    const chip = element('button', 'th-rel-chip th-rel-chip--expected');
    chip.type = 'button';
    chip.dataset.pop = `studio:${studio.id}`;
    chip.append(studioMark(studio), element('span', 'th-rel-chip__name', `~${dateRange(studio.expected)}`));
    return chip;
}

/** "oct 5–31", "sep 30 – oct 3", "oct 5". */
export function dateRange(range) {
    const start = shortDate(range.start);
    if (range.end === range.start) {
        return start;
    }
    if (range.end.slice(0, 7) === range.start.slice(0, 7)) {
        return `${start}–${Number(range.end.slice(8))}`;
    }
    return `${start} – ${shortDate(range.end)}`;
}

/** "72% within 30 days"; "" when unknown. */
export function chanceText(expected) {
    return expected.p30 === null ? '' : `${chancePercent(expected.p30)}% within 30 days`;
}

/**
 * Whole percent of an estimate, 1–99: an estimate is never certain, and 0.998 must not
 * read as "100%".
 * @param {number} share 0..1
 * @returns {number}
 */
export function chancePercent(share) {
    return Math.min(99, Math.max(1, Math.round(share * 100)));
}

/**
 * Details of a model: date, context, price, sizes, weights, benchmarks, sources.
 * @param {{now: Date, attribution: Object}} context today and the benchmark credit
 */
export function releaseDetails(release, studio, context) {
    const { now, attribution } = context;
    const box = element('div', 'th-rel-pop');
    box.append(popHead(studio, release.name, `${studio.name} · ${typeLabel(release)}`));
    const date = release.approx_date ? `≈ ${longDate(release.date)}` : longDate(release.date);
    const note = dateNote(release);
    const parts = [date, note.text, relativeDays(release.date, now)].filter(Boolean);
    const dateLine = element('p', 'th-rel-pop__date', parts.join(' · '));
    if (note.title) {
        dateLine.title = note.title;
    }
    box.append(dateLine);
    const facts = factList([
        ['API name', release.api_name],
        ['Model ID', release.model_id],
        [contextLabel(release), formatTokens(release.context)],
        ['Price', formatPrice(release)],
        ['Cached input', formatCachedPrice(release)],
        ['Access', release.access === 'limited' ? 'Limited — by application or approval' : ''],
        ['Sizes', release.sizes.join(' · ')],
        ['Open weights', openWeightsText(release.open_weights)],
        ['Stage', release.stage === 'preview' ? 'Preview' : ''],
    ]);
    if (facts) {
        box.append(facts);
    }
    if (facts && release.params_sources.length > 0) {
        box.append(paramsCredit(release.params_sources));
    }
    const benchmarks = benchmarkList(release, attribution);
    if (benchmarks) {
        box.append(benchmarks);
    }
    box.append(sourceLinks(release.links));
    return box;
}

/** "Details: Docs · Announcement" — where the id, context and price come from, as links. */
function paramsCredit(sources) {
    const credit = element('p', 'th-rel-pop__credit', 'Details: ');
    sources.forEach((source, index) => {
        if (index > 0) {
            credit.append(' · ');
        }
        credit.append(sourceLink({ name: source.kind, url: source.url }));
    });
    return credit;
}

const SPEECH_KIND_LABELS = {
    tts: 'text-to-speech',
    stt: 'speech-to-text',
    voice: 'voice (speech ↔ speech)',
};

/** "text", "image"…; a speech model says what it does: "text-to-speech", "speech-to-text". */
function typeLabel(release) {
    return SPEECH_KIND_LABELS[release.speech_kind] || release.type;
}

/**
 * Voiceover and recognition models take a limited input, not a conversation: "input limit";
 * voice (speech ↔ speech) and other models keep "context window".
 */
function contextLabel(release) {
    return release.speech_kind === 'tts' || release.speech_kind === 'stt' ? 'Input limit' : 'Context window';
}

function openWeightsText(value) {
    if (value === null || value === undefined) {
        return '';
    }
    return value ? 'Yes' : 'No';
}

function popHead(studio, title, subtitle) {
    const head = element('div', 'th-rel-pop__head');
    const text = element('div', 'th-rel-pop__titles');
    text.append(element('p', 'th-rel-pop__title', title), element('p', 'th-rel-pop__sub', subtitle));
    head.append(studioMark(studio), text);
    return head;
}

function factList(rows) {
    const shown = rows.filter(([, value]) => value);
    if (shown.length === 0) {
        return null;
    }
    const list = element('dl', 'th-rel-pop__facts');
    shown.forEach(([label, value]) => {
        list.append(element('dt', '', label), element('dd', '', value));
    });
    return list;
}

function benchmarkList(release, attribution) {
    const rows = benchmarkRows(release, attribution?.benchmark_sources || {});
    if (rows.length === 0) {
        return null;
    }
    const wrap = element('div', 'th-rel-pop__bench');
    wrap.append(element('p', 'th-rel-pop__label', 'Benchmarks'));
    rows.forEach((row) => wrap.append(benchmarkRow(row)));
    wrap.append(benchmarkCredit(release, attribution));
    return wrap;
}

const ROW_BY_UNIT = { rating: ratingRow, index: indexRow };

/**
 * The chosen tests ordered by the model's place, best first (owner, 30.09): #1 on top, the
 * weakest place at the bottom; of equal places the test with more models first (#1 of 268
 * before #1 of 5); tests without a place (a reported score) last, in the radio's order.
 * first — the model is #1 there (gold bar); leader — where the #1 result sits on the bar.
 */
function benchmarkRows(release, sources) {
    const items = (release.benchmarks || []).map((item) => ({
        item,
        row: ROW_BY_UNIT[sources[item.name]?.unit]?.(item) || shareRow(item),
    }));
    if (release.eci) {
        const eci = { ...release.eci, name: 'Epoch Capabilities Index' };
        items.unshift({ item: eci, row: indexRow(eci) });
    }
    return items
        .map((entry, order) => ({ ...entry, order }))
        .sort((a, b) => byPlace(a.item, b.item) || a.order - b.order)
        .map((entry) => entry.row);
}

/** Better place first: rank, then the share of the test's models ahead (lower is better). */
function byPlace(a, b) {
    if (!a.rank || !b.rank) {
        return (a.rank ? 0 : 1) - (b.rank ? 0 : 1);
    }
    return a.rank - b.rank || placeShare(a) - placeShare(b);
}

function placeShare(item) {
    return item.total ? item.rank / item.total : 1;
}

/** An index in points (Epoch Capabilities Index, Artificial Analysis): one decimal, place;
 * the bar is the share of the leader's index. */
function indexRow(item) {
    const place = item.rank ? placeText(item) : null;
    return { name: item.name, score: item.score.toFixed(1), place,
        share: item.best ? item.score / item.best : null, leader: null, first: item.rank === 1 };
}

/** A test scored as a share (0…1): percent, place and a bar with the leader's mark. */
function shareRow(item) {
    const first = item.rank === 1;
    return { name: item.name, score: `${(item.score * 100).toFixed(1)}%`,
        place: item.rank ? placeText(item) : null, share: item.score,
        leader: first || !item.rank ? null : item.best, first };
}

/** An arena rating (LMArena): points and place; the bar is the share of the arena leader's
 * points, like the capabilities index (the points have no top of scale of their own). */
function ratingRow(item) {
    const place = item.rank ? placeText(item) : null;
    return { name: item.name, score: String(Math.round(item.score)), place,
        share: item.best ? item.score / item.best : null, leader: null, first: item.rank === 1 };
}

function placeText(item) {
    return item.rank === 1 ? '#1' : `#${item.rank} of ${item.total}`;
}

/**
 * "benchmarks: Epoch AI (CC BY 4.0) · Supabase Evals (Apache-2.0) · reported by Qwen" — every
 * source whose test is in the window, each a link, once (three Artificial Analysis indexes —
 * one credit). A test of an open leaderboard names its own source
 * (attribution.benchmark_sources); a score from the model card, the studio that reported it
 * (link to the card); the index and the rest come from Epoch AI.
 */
function benchmarkCredit(release, attribution) {
    const leaderboards = attribution?.benchmark_sources || {};
    const shown = release.benchmarks || [];
    const names = shown.filter((item) => item.source !== 'reported').map((item) => item.name);
    const sources = [];
    if (release.eci || names.some((name) => !leaderboards[name])) {
        sources.push(attribution?.benchmarks?.url ? attribution.benchmarks : EPOCH_SOURCE);
    }
    names.filter((name) => leaderboards[name]).forEach((name) => {
        const source = leaderboards[name];
        if (!sources.some((known) => known.credit === source.credit)) {
            sources.push(source);
        }
    });
    sources.push(...reportedSource(shown));
    const credit = element('p', 'th-rel-pop__credit', 'Benchmarks: ');
    sources.forEach((source, index) => {
        if (index > 0) {
            credit.append(' · ');
        }
        credit.append(sourceLink(source));
    });
    return credit;
}

/** [{name: "reported by Qwen", url: model card}] when the window shows a score from the card. */
function reportedSource(shown) {
    const card = shown.find((item) => item.source === 'reported');
    return card ? [{ name: `reported by ${card.by || 'the studio'}`, url: card.url }] : [];
}

/**
 * The source as a short link ("Epoch AI", "AA via OpenRouter"); the full citation the source
 * asks for, or the name with the licence, is its tooltip — the licences are also in the page
 * text. Without a valid https address — plain text.
 */
function sourceLink(source) {
    const text = source.credit || source.name;
    const license = source.license ? ` (${source.license})` : '';
    const url = safeUrl(source.url);
    const node = element(url ? 'a' : 'span', '', text);
    node.title = source.citation || `${text}${license}`;
    if (url) {
        node.href = url;
        node.target = '_blank';
        node.rel = 'noopener';
    }
    return node;
}

/**
 * A row with a label, a value, an optional place and an optional bar: fill share (0…1),
 * an optional leader mark (0…1), and gold with a running light for first place.
 */
function benchmarkRow(row) {
    const item = element('div', row.first ? 'th-rel-bench th-rel-bench--first' : 'th-rel-bench');
    item.append(element('span', 'th-rel-bench__name', row.name), element('span', 'th-rel-bench__score', row.score));
    if (row.place) {
        item.append(element('span', 'th-rel-bench__rank', row.place));
    }
    if (row.share !== null && row.share !== undefined) {
        const bar = element('span', 'th-rel-bench__bar');
        const fill = element('span', 'th-rel-bench__fill');
        fill.style.setProperty('--share', String(clampShare(row.share)));
        bar.append(fill);
        if (row.leader !== null && row.leader !== undefined) {
            const leader = element('span', 'th-rel-bench__leader');
            leader.style.setProperty('--leader', String(clampShare(row.leader)));
            leader.title = `#1: ${(row.leader * 100).toFixed(1)}%`;
            bar.append(leader);
        }
        item.append(bar);
    }
    return item;
}

function clampShare(value) {
    return Math.max(0, Math.min(1, value));
}

function sourceLinks(links) {
    const line = element('p', 'th-rel-pop__links');
    (links || []).forEach((link) => {
        const url = safeUrl(link.url);
        if (url) {
            const anchor = element('a', '', `${link.label || link.kind} ↗`);
            anchor.href = url;
            anchor.target = '_blank';
            anchor.rel = 'noopener';
            line.append(anchor);
        }
    });
    return line;
}

/**
 * The studio's expected next release (any model of the studio — no name, owner 01.10): the
 * range, the chance within 30 days, and what the estimate rests on — the release rhythm and
 * each rumour or teaser with its share of the estimate (together 100%; without a rhythm the
 * rest is the chance the rumours are wrong).
 */
export function expectedDetails(studio) {
    const expected = studio.expected;
    const box = element('div', 'th-rel-pop');
    box.append(popHead(studio, studio.name, 'Expected next release'));
    const when = [dateRange(expected), chanceText(expected)].filter(Boolean).join(' · ');
    box.append(element('p', 'th-rel-pop__date', when));
    const basis = element('div', 'th-rel-pop__bench');
    basis.append(element('p', 'th-rel-pop__label', 'Based on'));
    const parts = basisParts(expected);
    const percents = wholePercents(parts.map((part) => part.share));
    parts.forEach((part, index) => {
        basis.append(benchmarkRow({ name: part.name, score: `${percents[index]}%`, place: '', share: part.share }));
        const url = safeUrl(part.url);
        if (url) {
            basis.append(sourceLinks([{ kind: 'source', label: 'Source', url }]));
        }
    });
    box.append(basis, element('p', 'th-rel-pop__credit', 'An estimate from release history and rumors, not an announcement'));
    return box;
}

/** What the estimate rests on, shares together 1: rhythm, each rumour, and — for a studio
 * without a rhythm — the chance the rumours are wrong. */
function basisParts(expected) {
    const parts = [];
    if (expected.habit) {
        parts.push({ name: `Release rhythm: about every ${expected.habit.typical_days} days`, share: expected.habit.weight });
    }
    expected.signals.forEach((signal) => {
        const name = `${capitalize(signal.type.replace('_', ' '))}: ${windowText(signal)}`;
        parts.push({ name, share: signal.share, url: signal.source_url });
    });
    if (expected.unsure > 0) {
        parts.push({ name: 'The rumors turn out wrong', share: expected.unsure });
    }
    return parts;
}


/**
 * Whole percents that add up to exactly 100 (largest remainder): 1/3 × 3 is 34 + 33 + 33,
 * not 33 × 3 = 99.
 * @param {number[]} shares parts of one whole, summing to about 1
 * @returns {number[]}
 */
export function wholePercents(shares) {
    const total = shares.reduce((sum, share) => sum + share, 0) || 1;
    const exact = shares.map((share) => (share / total) * 100);
    const percents = exact.map(Math.floor);
    const order = exact
        .map((value, index) => [value - percents[index], index])
        .sort((first, second) => second[0] - first[0]);
    let missing = 100 - percents.reduce((sum, value) => sum + value, 0);
    for (const [, index] of order) {
        if (missing <= 0) {
            break;
        }
        percents[index] += 1;
        missing -= 1;
    }
    return percents;
}

function windowText(signal) {
    return dateRange({ start: signal.window_start, end: signal.window_end });
}
